import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/pagination";
import { SalonEditDialog, type SalonItem } from "@/components/salon-edit-dialog";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Plus, Search, Users } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/salons/")({
  component: AdminSalonsPage,
});

const statusMap = {
  active: { label: "Aktywny", variant: "default" as const },
  inactive: { label: "Nieaktywny", variant: "secondary" as const },
  suspended: { label: "Zawieszony", variant: "destructive" as const },
};

const nextStatus: Record<string, "active" | "inactive" | "suspended"> = {
  active: "inactive",
  inactive: "active",
  suspended: "active",
};

function AdminSalonsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SalonItem | null>(null);

  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.admin.listSalons.useQuery({
    page,
    limit: 20,
    search: search || undefined,
  });

  const toggleStatus = trpc.admin.updateSalon.useMutation({
    onSuccess: () => {
      utils.admin.listSalons.invalidate();
      toast.success("Status zmieniony");
    },
    onError: (err) => toast.error(err.message),
  });

  const totalPages = Math.ceil((data?.total ?? 0) / 20);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Salony</h1>
          <p className="text-sm text-muted-foreground">
            {data?.total ?? 0} salonów
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-1 size-4" />
          Nowy salon
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Szukaj po nazwie..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Ładowanie...</p>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Nazwa</th>
                  <th className="px-4 py-3 font-medium">Kod</th>
                  <th className="px-4 py-3 font-medium">Organizacja</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Tpay</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Licencja do</th>
                  <th className="px-4 py-3 font-medium">Utworzony</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((salon) => {
                  const status = statusMap[salon.status];
                  return (
                    <tr
                      key={salon.id}
                      className={
                        salon.hasTpayAccount
                          ? "border-b last:border-0"
                          : "border-b border-l-4 border-l-red-500 bg-red-50/60 last:border-b-0 dark:bg-red-950/20"
                      }
                    >
                      <td className="px-4 py-3 font-medium">{salon.name}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {salon.code}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {salon.adminOrgName ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={status.variant}
                          className="cursor-pointer"
                          onClick={() =>
                            toggleStatus.mutate({
                              salonId: salon.id,
                              status: nextStatus[salon.status],
                            })
                          }
                        >
                          {status.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {salon.hasTpayAccount ? (
                          <Badge
                            variant="outline"
                            className="gap-1 border-green-600 text-green-700 dark:text-green-400"
                          >
                            <CheckCircle2 className="size-3" />
                            Konto partnera
                          </Badge>
                        ) : (
                          <Badge
                            variant="destructive"
                            className="gap-1 font-semibold"
                          >
                            <AlertTriangle className="size-3" />
                            Brak konta
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {salon.email ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {salon.licenseExpiresAt
                          ? new Date(
                              salon.licenseExpiresAt,
                            ).toLocaleDateString("pl-PL")
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(salon.createdAt).toLocaleDateString("pl-PL")}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditing(salon);
                              setDialogOpen(true);
                            }}
                          >
                            Edytuj
                          </Button>
                          <Link
                            to="/admin/salons/$id"
                            params={{ id: salon.id }}
                          >
                            <Button variant="ghost" size="sm">
                              <Users className="size-4" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {data?.items.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      Brak salonów
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-3">
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </Card>
      )}

      <SalonEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        salon={editing}
      />
    </div>
  );
}
